import { useState } from "react";
import { useQuery, useMutation } from "react-query";
import { useDispatch } from "react-redux";

import { getErrorMessage } from "helpers";
import { uiActions } from "store";
import { ErrorResponse, AnyNote, NoteMutationParams } from "types";

import { NoteServiceContextType } from "./notesServiceContext";

export type UseMutationParams = {
	onSuccessCb?: (result?: AnyNote) => any;
	onErrorCb?: (error?: ErrorResponse) => void;
};

const useNoteService = ({ key, service }: NoteServiceContextType) => {
	const dispatch = useDispatch();
	const [page, setPage] = useState(0);

	const handleShowError = (error: ErrorResponse) => {
		dispatch(
			uiActions.showErrorNotification({
				title: getErrorMessage(error),
			}),
		);
	};

	const useNoteListQuery = ({ entityId, ...filters }) => {
		const fetchDealNotes = () => {
			dispatch(uiActions.showSpinnerOverlay());
			return service
				.getNotes(entityId, { page, ...filters })
				.finally(() => {
					dispatch(uiActions.hideSpinnerOverlay());
				});
		};

		const queryResult = useQuery([key, page, filters], fetchDealNotes, {
			keepPreviousData: true,
			enabled: !!entityId,
		});

		return {
			...queryResult,
			page,
			setPage,
		};
	};

	const useAddNoteMutation = ({
		onSuccessCb,
		onErrorCb,
	}: UseMutationParams = {}) => {
		return useMutation(
			({ entityId, note }: NoteMutationParams) => {
				dispatch(uiActions.showSpinnerOverlay());
				return service.addNote(entityId, note);
			},
			{
				onSettled() {
					dispatch(uiActions.hideSpinnerOverlay());
				},
				onSuccess(newNote: AnyNote) {
					dispatch(
						uiActions.showSuccessNotification({
							title: "Note successfully added",
						}),
					);

					onSuccessCb?.(newNote);
				},
				onError(error: ErrorResponse) {
					handleShowError(error);
					onErrorCb?.(error);
				},
			},
		);
	};

	const useEditNoteMutation = ({
		onSuccessCb,
		onErrorCb,
	}: UseMutationParams = {}) => {
		return useMutation(
			({ entityId, note }: NoteMutationParams) => {
				dispatch(uiActions.showSpinnerOverlay());
				return service.editNote(entityId, note);
			},
			{
				onSettled() {
					dispatch(uiActions.hideSpinnerOverlay());
				},
				onSuccess(updatedNote: AnyNote) {
					dispatch(
						uiActions.showSuccessNotification({
							title: "Note successfully updated",
						}),
					);

					onSuccessCb?.(updatedNote);
				},
				onError(error: ErrorResponse) {
					handleShowError(error);
					onErrorCb?.(error);
				},
			},
		);
	};

	const useDeleteNoteMutation = ({
		onSuccessCb,
		onErrorCb,
	}: UseMutationParams = {}) => {
		return useMutation(
			({ entityId, note }: NoteMutationParams) => {
				dispatch(uiActions.showSpinnerOverlay());
				return service.deleteNote(entityId, note);
			},
			{
				onSettled() {
					dispatch(uiActions.hideSpinnerOverlay());
				},
				onSuccess(_response, { note: deletedNote }) {
					dispatch(
						uiActions.showSuccessNotification({
							title: "Note successfully deleted",
						}),
					);

					onSuccessCb?.(deletedNote);
				},
				onError(error: ErrorResponse) {
					handleShowError(error);
					onErrorCb?.(error);
				},
			},
		);
	};

	return {
		useNoteListQuery,
		useAddNoteMutation,
		useEditNoteMutation,
		useDeleteNoteMutation,
	};
};

export default useNoteService;
